import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Flame,
  PenLine,
  LogOut,
  Trash2,
  Feather,
  Search,
  ChevronRight,
  Play,
  Pause,
  Loader2,
  AudioLines,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE, formatApiError } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function wordCount(text) {
  if (!text) return 0;
  return text
    .replace(/<[^>]*>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
}

const VOICE_OPTIONS = [
  { id: "pNInz6obpgDQGcFmaJgB", label: "Adam", note: "Deep · storyteller" },
  { id: "ErXwobaYiN019PkySvjV", label: "Antoni", note: "Warm · narrative" },
  { id: "TxGEqnHWrfWFTfGW9XjX", label: "Josh", note: "Soft · calm" },
  { id: "EXAVITQu4vr4xnSDxMaL", label: "Bella", note: "Gentle female" },
  { id: "21m00Tcm4Tlm9pMXn7Iu", label: "Rachel", note: "Clear female" },
];

const VOICE_STORAGE_KEY = "kkh:tts:voice";
const DEFAULT_VOICE = "pNInz6obpgDQGcFmaJgB";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [drafts, setDrafts] = useState(null);
  const [query, setQuery] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [voice, setVoice] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_VOICE;
    return localStorage.getItem(VOICE_STORAGE_KEY) || DEFAULT_VOICE;
  });
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(VOICE_STORAGE_KEY, voice);
  }, [voice]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlayingId(null);
  }, []);

  const playDraft = useCallback(
    async (draft) => {
      if (playingId === draft.id) {
        stopAudio();
        return;
      }
      stopAudio();
      const text = `${draft.title?.trim() ? draft.title.trim() + ". " : ""}${draft.body?.trim() || ""}`;
      if (!text.trim()) {
        toast.info("This draft is empty — nothing to read yet.");
        return;
      }
      setLoadingId(draft.id);
      try {
        const res = await fetch(`${API_BASE}/tts/speak`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: text.slice(0, 4000), voice, model: "tts-1" }),
        });
        if (!res.ok) {
          let msg = "Could not read this draft aloud.";
          try {
            const j = await res.json();
            if (j?.detail) msg = typeof j.detail === "string" ? j.detail : msg;
          } catch (_e) {}
          throw new Error(msg);
        }
        const detectedUrdu = res.headers.get("x-kkh-detected-urdu") === "1";
        const appliedVoice = res.headers.get("x-kkh-voice-applied") || voice;
        const provider = res.headers.get("x-kkh-provider") || "openai";
        const fallbackReason = res.headers.get("x-kkh-fallback-reason") || "";
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.addEventListener("ended", () => {
          stopAudio();
        });
        audio.addEventListener("error", () => {
          stopAudio();
          toast.error("Playback failed.");
        });
        await audio.play();
        setPlayingId(draft.id);
        if (fallbackReason === "eleven_free_tier") {
          toast("ElevenLabs free tier cannot use library voices via API", {
            description:
              "Playing with OpenAI for now. Add your chosen voice to 'My Voices' at elevenlabs.io/app/voice-library, or upgrade to Starter.",
          });
        } else if (fallbackReason === "eleven_bad_key") {
          toast.error("ElevenLabs API key invalid — using OpenAI fallback.");
        } else if (fallbackReason === "eleven_voice_missing") {
          toast("Voice not in your ElevenLabs account", {
            description: "Add the voice to 'My Voices' on ElevenLabs first, or the app will keep using OpenAI.",
          });
        } else if (fallbackReason && fallbackReason !== "") {
          toast("Using OpenAI fallback for read-aloud.");
        } else if (detectedUrdu && provider === "elevenlabs") {
          toast("Roman-Urdu detected · reading in a Hindustani voice");
        } else if (detectedUrdu) {
          toast("Roman-Urdu detected · using deep OpenAI voice", {
            description: "For an authentic Hindustani voice, add an Urdu/Hindi voice to your ElevenLabs 'My Voices'.",
          });
        }
      } catch (err) {
        toast.error(err?.message || "Could not read this draft aloud.");
      } finally {
        setLoadingId(null);
      }
    },
    [playingId, stopAudio, voice],
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/drafts");
      setDrafts(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDrafts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createNew = async () => {
    try {
      const { data } = await api.post("/drafts", { title: "", body: "" });
      navigate(`/app/write/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doLogout = async () => {
    await logout();
    toast.success("Take care. The room stays here.");
    navigate("/", { replace: true });
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/drafts/${confirmDelete.id}`);
      setDrafts((d) => d.filter((x) => x.id !== confirmDelete.id));
      toast.success("Draft removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = (drafts || []).filter((d) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (d.title || "").toLowerCase().includes(q) ||
      (d.body || "").toLowerCase().includes(q)
    );
  });

  const totalWords = (drafts || []).reduce((acc, d) => acc + wordCount(d.body), 0);

  return (
    <div
      className="min-h-screen text-stone-100 grain bg-cover bg-center bg-no-repeat bg-fixed"
      style={{
        backgroundImage:
          "url('https://customer-assets-v7afamib.emergentagent.net/wingman/4b71e223-37e4-4615-8453-e68a2ac08d9e/attachments/2326fe456d6d4d5388c8c1de34043b45_ChatGPT%20Image%20Sep%204%2C%202026%2C%2004_50_11%20PM.png')",
      }}
      data-testid="dashboard-root"
    >
      {/* Top bar */}
      <header className="relative z-10 border-b border-stone-900/60 bg-stone-950/70 backdrop-blur-xl sticky top-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer group select-none"
            data-testid="dashboard-brand-home-link"
            title="Back to homepage"
          >
            <Flame className="w-5 h-5 text-amber-400 candle-flicker" />
            <span className="font-serif-editorial text-xl tracking-wide group-hover:text-amber-200 transition-colors">
              Kuch Kahna Hai
            </span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="dashboard-profile-menu-btn"
                className="inline-flex items-center gap-3 rounded-full bg-stone-900/70 border border-stone-800 hover:border-amber-500/50 px-3 py-1.5 pr-4 transition-all"
              >
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 grid place-items-center text-stone-950 text-xs font-bold">
                  {(user?.pseudonym || "?").slice(0, 1).toUpperCase()}
                </span>
                <span
                  className="text-sm text-stone-200"
                  data-testid="dashboard-pseudonym-badge"
                >
                  {user?.pseudonym}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-stone-900 border-stone-800 text-stone-100 min-w-[220px]"
            >
              <DropdownMenuLabel>
                <div className="text-xs text-stone-400 font-mono-mini uppercase tracking-widest">
                  Signed in as
                </div>
                <div className="text-sm text-stone-100 mt-1">{user?.pseudonym}</div>
                <div className="text-xs text-stone-500 truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-stone-800" />
              <DropdownMenuItem
                data-testid="dashboard-logout-btn"
                onClick={doLogout}
                className="cursor-pointer text-rose-300 focus:text-rose-200 focus:bg-rose-950/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        {/* Greeting */}
        <section className="mb-14 rise-in">
          <div className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-300/80 mb-3">
            Writing as {user?.pseudonym}
          </div>
          <h1 className="font-serif-editorial text-4xl sm:text-5xl leading-tight text-stone-50">
            What do you want to say tonight,
            <span className="italic text-amber-300"> {user?.pseudonym}</span>?
          </h1>
          <p className="mt-4 text-stone-400 max-w-xl">
            Your drafts stay yours. Nothing is published — this is just the page
            listening.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={createNew}
              data-testid="dashboard-new-draft-btn"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold px-6 py-3 transition-all shadow-[0_0_30px_-10px_rgba(245,158,11,0.6)]"
            >
              <PenLine className="w-4 h-4" />
              Write a new story
            </button>
            <div className="flex items-center gap-6 text-sm text-stone-400 font-mono-mini">
              <span>
                <span className="text-amber-300">{drafts?.length ?? "—"}</span>{" "}
                drafts
              </span>
              <span className="hidden sm:inline">
                <span className="text-amber-300">{totalWords}</span> words written
              </span>
            </div>
          </div>
        </section>

        {/* Drafts */}
        <section>
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <h2 className="font-serif-editorial text-2xl text-stone-100">
              Your drafts
            </h2>
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="dashboard-voice-picker-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-stone-900/70 border border-stone-800 hover:border-amber-500/60 px-4 py-2 text-sm text-stone-200 transition-all"
                  >
                    <AudioLines className="w-4 h-4 text-amber-300" />
                    <span className="font-mono-mini text-[10px] uppercase tracking-[0.24em] text-stone-400">
                      Voice
                    </span>
                    <span
                      className="text-stone-100"
                      data-testid="dashboard-voice-current-label"
                    >
                      {VOICE_OPTIONS.find((v) => v.id === voice)?.label || voice}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-stone-900 border-stone-800 text-stone-100 min-w-[220px]"
                >
                  <DropdownMenuLabel>
                    <div className="text-xs text-stone-400 font-mono-mini uppercase tracking-widest">
                      Read-aloud voice
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-stone-800" />
                  <DropdownMenuRadioGroup
                    value={voice}
                    onValueChange={(v) => {
                      setVoice(v);
                      stopAudio();
                    }}
                  >
                    {VOICE_OPTIONS.map((v) => (
                      <DropdownMenuRadioItem
                        key={v.id}
                        value={v.id}
                        data-testid={`dashboard-voice-option-${v.id}`}
                        className="cursor-pointer focus:bg-stone-800 focus:text-amber-200"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm">{v.label}</span>
                          <span className="text-[10px] text-stone-500 font-mono-mini uppercase tracking-widest">
                            {v.note}
                          </span>
                        </div>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              {drafts && drafts.length > 0 && (
                <div className="relative w-full max-w-xs">
                  <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="dashboard-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search drafts"
                    className="w-full bg-stone-900/70 border border-stone-800 focus:border-amber-500 outline-none rounded-full pl-9 pr-4 py-2 text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          {drafts === null && (
            <div className="grid gap-4">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-28 rounded-2xl bg-stone-900/50 border border-stone-800 animate-pulse"
                />
              ))}
            </div>
          )}

          {drafts && drafts.length === 0 && (
            <div
              className="rounded-3xl border border-dashed border-amber-900/40 bg-stone-900/30 p-12 text-center rise-in-delay-1"
              data-testid="dashboard-empty-state"
            >
              <Feather className="w-8 h-8 text-amber-400/80 mx-auto mb-4 candle-flicker" />
              <h3 className="font-serif-editorial text-2xl text-stone-100">
                You haven’t written anything yet.
              </h3>
              <p className="mt-3 text-stone-400 max-w-md mx-auto">
                Start with a single sentence. It doesn’t need to be good — it just
                needs to be true.
              </p>
              <button
                onClick={createNew}
                data-testid="dashboard-empty-cta-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold px-6 py-3 transition-all"
              >
                <PenLine className="w-4 h-4" />
                Open a blank page
              </button>
            </div>
          )}

          {drafts && drafts.length > 0 && (
            <ul className="grid gap-4">
              {filtered.map((d, i) => (
                <li
                  key={d.id}
                  data-testid="draft-card-item"
                  onClick={() => navigate(`/app/write/${d.id}`)}
                  className="group cursor-pointer flex items-center gap-6 rounded-2xl bg-stone-900/60 border border-amber-900/20 hover:border-amber-500/40 hover:bg-stone-900/90 p-5 sm:p-6 transition-all duration-300"
                  style={{ animation: `rise 0.55s ease-out ${i * 0.05}s both` }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono-mini text-[10px] uppercase tracking-[0.28em] text-amber-300/70">
                        {relativeTime(d.updated_at)}
                      </span>
                      <span className="font-mono-mini text-[10px] text-stone-600">
                        · {wordCount(d.body)} words
                      </span>
                    </div>
                    <h3 className="font-serif-editorial text-xl sm:text-2xl text-stone-50 truncate">
                      {d.title?.trim() || "Untitled draft"}
                    </h3>
                    <p className="mt-1 text-sm text-stone-400 line-clamp-2">
                      {d.body?.trim() || "Empty page. Ready when you are."}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        playDraft(d);
                      }}
                      data-testid="draft-play-btn"
                      className={`p-2 rounded-full transition-all ${
                        playingId === d.id
                          ? "text-amber-300 bg-amber-500/10"
                          : "text-stone-400 hover:text-amber-300 hover:bg-stone-900"
                      } ${loadingId === d.id ? "opacity-70 cursor-wait" : ""}`}
                      aria-label={playingId === d.id ? "Stop reading" : "Read aloud"}
                      title={playingId === d.id ? "Stop reading" : "Read aloud"}
                      disabled={loadingId === d.id}
                    >
                      {(() => {
                        if (loadingId === d.id) return <Loader2 className="w-4 h-4 animate-spin" />;
                        if (playingId === d.id) return <Pause className="w-4 h-4" />;
                        return <Play className="w-4 h-4" />;
                      })()}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(d);
                      }}
                      data-testid="draft-delete-btn"
                      className="p-2 rounded-full text-stone-500 hover:text-rose-300 hover:bg-rose-950/40 transition-all"
                      aria-label="Delete draft"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-stone-500 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="text-sm text-stone-500 text-center py-8">
                  No drafts match “{query}”.
                </li>
              )}
            </ul>
          )}
        </section>
      </main>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-stone-900 border-stone-800 text-stone-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-editorial text-2xl">
              Delete this draft?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              This can’t be undone. The words go with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="draft-delete-cancel-btn"
              className="bg-stone-800 border-stone-700 text-stone-100 hover:bg-stone-700 hover:text-stone-50"
            >
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="draft-delete-confirm-btn"
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              Delete draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
